import type { UIMessage } from 'ai';

/**
 * The button-origin marker on a user message (`metadata.source`, FRONT-199)
 * and the words it shows INSTEAD of the instruction text the button sent.
 * Dependency-free so the desktop bubble, the phone bubble and the
 * conversation export all read the same words - never a copy of the strings.
 */
export interface MessageSourceMeta {
  kind: 'button';
  action: string;
  label: string;
}

/** Read `metadata.source`; null for a normal typed message. */
export function readSourceMeta(message: UIMessage): MessageSourceMeta | null {
  const meta = (message as unknown as { metadata?: { source?: unknown } }).metadata;
  const s = meta?.source as { kind?: unknown; action?: unknown; label?: unknown } | undefined;
  if (!s || s.kind !== 'button' || typeof s.action !== 'string' || typeof s.label !== 'string') {
    return null;
  }
  return { kind: 'button', action: s.action, label: s.label };
}

/**
 * Map a button action to its in-progress / settled wording. Falls back to the
 * stored label when an action id isn't recognised.
 */
export function actionChipLabels(source: MessageSourceMeta): { running: string; done: string } {
  switch (source.action) {
    case 'update-document':
      return { running: 'Updating document…', done: 'Updated document' };
    case 'save-graph':
      return { running: 'Saving to your knowledge graph…', done: 'Saved to knowledge graph' };
    case 'save-document':
      return { running: 'Saving as a document…', done: 'Saved as document' };
    // FRONT-3036: the turn the SERVER starts after a late-approved step settles.
    case LATE_APPROVAL_CONTINUATION:
      return { running: 'Picking up where I paused…', done: 'Picked up where I paused' };
    default:
      return { running: `${source.label}…`, done: source.label };
  }
}

/** The action id of the turn the SERVER starts after a late-approved step settles. */
export const LATE_APPROVAL_CONTINUATION = 'late-approval-continuation';

/** True for the server-started continuation message (map by ACTION, never by label). */
export function isLateApprovalContinuation(source: MessageSourceMeta | null): boolean {
  return source?.action === LATE_APPROVAL_CONTINUATION;
}

/**
 * The chip wording for the late-approval continuation message ONLY, else null.
 * Used by the phone note and the conversation export; every other button
 * source keeps its previous reader behaviour (stored label on the phone, the
 * message's own text in the export).
 */
export function continuationChipText(message: UIMessage): string | null {
  const source = readSourceMeta(message);
  return source && isLateApprovalContinuation(source) ? actionChipLabels(source).done : null;
}

/**
 * FRONT-2991: a request a voice call handed to EDDY. Unlike a button chip,
 * the person's own words ARE the message, so the bubble shows them as typed
 * text with a small "From the call" mark — never the chip in their place.
 */
export const VOICE_CALL_SOURCE = 'voice-call';

export function isFromCall(source: MessageSourceMeta | null): boolean {
  return source?.action === VOICE_CALL_SOURCE;
}
